import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar, { SidebarItem } from "./Sidebar";
import useSignOut from "react-auth-kit/hooks/useSignOut";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import { LogOut, Home, Map, Grid, UserPlus, LayoutDashboard } from "lucide-react";
import BuildIcon from '@mui/icons-material/Build';
import FeedbackIcon from "@mui/icons-material/Feedback";
import HandymanIcon from '@mui/icons-material/Handyman';
import InventoryIcon from '@mui/icons-material/Inventory2';
import Divider from "@mui/material/Divider";

import axiosInstance, { publicInstance } from "../Pages/axiosInstance";
import { useRefresh } from "../Components/RefreshContext";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { darkTheme } from "../helpers/theme";
import { CssBaseline, useMediaQuery, GlobalStyles } from "@mui/material";
import Button from "@mui/material/Button";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useAtom } from 'jotai';
import { feedbackCountAtom, serviceCommentsCountAtom, shipmentsCountAtom, completedShipmentsCountAtom } from '../helpers/atoms';
import TopLoadingBar from '../Components/LoadingBar';
import posthog from 'posthog-js';
import MapIcon from '@mui/icons-material/Map';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import ExploreIcon from '@mui/icons-material/Explore';
import SearchIcon from '@mui/icons-material/Search';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ForumIcon from '@mui/icons-material/Forum';
import PeopleIcon from '@mui/icons-material/People';



const Layout: React.FC = () => {
  const [openFeedback, setOpenFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [feedbackCount, setFeedbackCount] = useAtom(feedbackCountAtom);
  const [serviceCommentsCount, setServiceCommentsCount] = useAtom(serviceCommentsCountAtom);
  const [shipmentsCount, setShipmentsCount] = useAtom(shipmentsCountAtom);
  const [completedShipmentsCount, setCompletedShipmentsCount] = useAtom(completedShipmentsCountAtom);
  const theme = darkTheme; // Always use dark theme
  const signOut = useSignOut();
  const navigate = useNavigate();
  const location = useLocation();
  const [role, setRole] = useState<string | null>(null);
  const [refreshFeedback, setRefreshFeedback] = useState(false);
  const { refresh } = useRefresh();
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    // Always use dark theme - remove theme switching logic
    localStorage.setItem("theme", "dark");

    const storedRole = localStorage.getItem("role");
    setRole(storedRole);

    publicInstance.get('/dacapi_shipment_of_devices')
      .then(response => {
        const uniqueGroupIds = countUniqueGroupIds(response.data);
        setShipmentsCount(uniqueGroupIds);
        const uniqueCompletedGroupIds = countCompletedGroupIds(response.data);
        setCompletedShipmentsCount(uniqueCompletedGroupIds);
      })
      .catch(error => console.error('Error fetching shipments count:', error));

    const isAdmin = storedRole === 'admin';
    const endpoint = isAdmin ?
      '/feedback/main-threads?skip=0&limit=10' :
      '/feedback/non-admin-threads?skip=0&limit=10';

    axiosInstance.get(endpoint)
      .then(response => {
        setFeedbackCount(response.data.total);
      })
      .catch(error => console.error('Error fetching feedback count:', error));

    publicInstance.get('/view_dacapi_sensorbox_service_comments')
      .then(response => {
        setServiceCommentsCount(response.data.length);
      })
      .catch(error => console.error('Error fetching service comments count:', error));
  }, [setFeedbackCount, setServiceCommentsCount, setShipmentsCount, setCompletedShipmentsCount]);

  useEffect(() => {
    posthog.capture('page_view', {
      username: localStorage.getItem('username'),
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
      referrer: document.referrer,
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      user_agent: navigator.userAgent
    });
  }, [window.location.pathname]);

  const countUniqueGroupIds = (data) => {
    const filteredData = data.filter(item => item.status === 'utworzone');
    const uniqueGroupIds = new Set(filteredData.map(item => item.group_id));
    return uniqueGroupIds.size;
  };

  const countCompletedGroupIds = (data) => {
    const filteredData = data.filter(item => item.status === 'skompletowane');
    const uniqueGroupIds = new Set(filteredData.map(item => item.group_id));
    return uniqueGroupIds.size;
  };

  const handleSignOut = () => {
    posthog.capture('user_signed_out', {
      username: localStorage.getItem("username"),
      timestamp: new Date().toISOString()
    });
    signOut();
    localStorage.removeItem("role");
    navigate("/login");
  };

  const handleOpenFeedback = () => setOpenFeedback(true);
  const handleCloseFeedback = () => {
    setOpenFeedback(false);
    setFeedback("");
  };
  const handleSnackbarClose = () => setOpenSnackbar(false);

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) {
      setSnackbarMessage("Feedback cannot be empty. Please enter your message.");
      setOpenSnackbar(true);
      return;
    }

    try {
      const response = await axiosInstance.post("/feedback/create/", {
        comment: feedback
      });

      if (response.data) {
        setSnackbarMessage("Feedback sent successfully!");
        setFeedback("");
        handleCloseFeedback();
        setRefreshFeedback(prev => !prev);
        setFeedbackCount(prevCount => prevCount + 1);
      }
    } catch (error) {
      console.error('Error sending feedback:', error);
      setSnackbarMessage("Failed to send feedback. Please try again.");
    }
    setOpenSnackbar(true);
  };

  // Theme switching removed - always use dark theme

  const updateFeedbackCount = (count: number) => {
    setFeedbackCount(count);
  };

  const updateServiceCommentsCount = (count: number) => {
    setServiceCommentsCount(count);
  };

  const updateShipmentsCount = (count: number) => {
    setShipmentsCount(count);
  };



  const refreshFeedbackList = () => {
    setRefreshFeedback((prev) => !prev);
  };

  const handleRepair = (box: number) => {
    console.log(`Repair handled for box: ${box}`);
    setServiceCommentsCount(prevCount => prevCount - 1);
  };

  const feedbackModalBody = (
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 600,
        bgcolor: "background.paper",
        boxShadow: 24,
        p: 4,
        outline: "none",
      }}
    >
      <Typography variant="h6" component="h2">
        Send Feedback
      </Typography>
      <TextField
        autoFocus
        margin="dense"
        id="feedback"
        label="Your Feedback"
        type="text"
        fullWidth
        variant="outlined"
        multiline
        rows={4}
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 2 }}>
        <Button onClick={handleCloseFeedback}>Cancel</Button>
        <Button
          onClick={handleFeedbackSubmit}
          disabled={!feedback.trim()}
          variant="contained"
        >
          Submit
        </Button>
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TopLoadingBar />
      <GlobalStyles styles={(theme: any) => ({
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          background: theme.palette.background.default,
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.primary.main,
          borderRadius: '8px',
          border: `2px solid ${theme.palette.background.default}`,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
        'a': {
          textDecoration: 'none',
          color: 'inherit',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            transform: 'translateX(4px)',
          },
          '&:active': {
            backgroundColor: theme.palette.action.selected,
          },
          '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
          },
        },
      })} />
      <div className="layout" style={{ display: 'flex', minHeight: '100vh' }}>
        <Sidebar handleOpenFeedback={handleOpenFeedback}>
          <>
            {role === "instalator" ? (
              <>
                <Link to="/installation">
                  <SidebarItem
                    icon={<EngineeringIcon />}
                    text="Installation Page"
                    active={location.pathname === "/installation"}
                  />
                </Link>
                <Link onClick={handleSignOut} to="/login">
                  <SidebarItem
                    icon={<LogOut />}
                    text="Log out"
                    active={false}
                  />
                </Link>
              </>
            ) : (
              <>
                <Link to="/">
                  <SidebarItem
                    icon={<Home />}
                    text="Home"
                    active={location.pathname === "/"}
                  />
                </Link>
                {/* <Link to="/dashboard">
                  <SidebarItem
                    icon={<LayoutDashboard />}
                    text="Dashboard"
                    active={location.pathname === "/dashboard"}
                  />
                </Link> */}
                <Link to="/data">
                  <SidebarItem
                    icon={<Grid />}
                    text="Data Tables"
                    active={location.pathname === "/data"}
                  />
                </Link>



                {(role === "admin" || role === "serwis+" || role === "serwis") && (
                  <Link to="/installation">
                    <SidebarItem
                      icon={<EngineeringIcon />}
                      text="Installation Page"
                      active={location.pathname === "/installation"}
                    />
                  </Link>
                )}

                <Divider sx={{ my: 1, mx: 2, opacity: 0.3 }} />

                <SidebarItem
                  icon={<MapIcon />}
                  text="Map"
                  active={location.pathname.startsWith('/map')}
                  subItems={[
                    {
                      text: "Service Map",
                      path: "/map/service",
                      active: location.pathname === "/map/service" || location.pathname.startsWith('/map/service/'),
                      icon: <HandymanIcon />
                    },
                    // {
                    //   text: "Map View",
                    //   path: "/map/view",
                    //   active: location.pathname === "/map/view",
                    //   icon: <MapIcon />
                    // },
                    {
                      text: "BTS Search",
                      path: "/map/bts",
                      active: location.pathname === "/map/bts",
                      icon: <SettingsInputAntennaIcon />
                    }
                  ]}
                />

                <Divider sx={{ my: 1, mx: 2, opacity: 0.3 }} />

                <SidebarItem
                  icon={<BuildIcon />}
                  text="Service"
                  active={location.pathname.startsWith('/repairs') || location.pathname.startsWith('/shipments') || location.pathname.startsWith('/inventory')}
                  subItems={[
                    {
                      text: `Repair Panel (${serviceCommentsCount})`,
                      path: "/repairs",
                      active: location.pathname === "/repairs",
                      icon: <BuildIcon />
                    },
                    {
                      text: `Shipments (${shipmentsCount} | ${completedShipmentsCount})`,
                      path: "/shipments",
                      active: location.pathname === "/shipments",
                      icon: <LocalShippingIcon />
                    },
                    {
                      text: "Inventory",
                      path: "/inventory",
                      active: location.pathname === "/inventory",
                      icon: <InventoryIcon />
                    }
                  ]}
                />

                {(role === "admin") && (
                  <>
                    <Divider sx={{ my: 1, mx: 2, opacity: 0.3 }} />
                    <SidebarItem
                      icon={<UserPlus />}
                      text="Admin"
                      active={location.pathname === "/admin"}
                      subItems={[
                        {
                          text: "Admin Panel",
                          path: "/admin",
                          active: location.pathname === "/admin",
                          icon: <UserPlus />
                        },

                      ]}
                    />
                  </>
                )}

                <Divider sx={{ my: 1, mx: 2, opacity: 0.3 }} />

                <SidebarItem
                  icon={<PeopleIcon />}
                  text="Community"
                  active={location.pathname.startsWith('/feedback') || location.pathname.startsWith('/forum')}
                  subItems={[
                    {
                      text: `Feedback (${feedbackCount})`,
                      path: "/feedback",
                      active: location.pathname === "/feedback",
                      icon: <FeedbackIcon />
                    },
                    {
                      text: "Forum",
                      path: "/forum",
                      active: location.pathname === "/forum",
                      icon: <ForumIcon />
                    }
                  ]}
                />

                <Divider sx={{ my: 1, mx: 2, opacity: 0.3 }} />

                <Link onClick={handleSignOut} to="/login">
                  <SidebarItem
                    icon={<LogOut />}
                    text="Log out"
                    active={false}
                  />
                </Link>
              </>
            )}
          </>
        </Sidebar>
        <div className="main-content" style={{ flexGrow: 1, marginTop: isMobile ? '32px' : '0' }}>
          <Outlet context={{ handleRepair, updateFeedbackCount, updateServiceCommentsCount, updateShipmentsCount, refreshFeedbackList, refreshFeedback }} />
        </div>
        <Modal
          open={openFeedback}
          onClose={handleCloseFeedback}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          {feedbackModalBody}
        </Modal>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
        />
      </div>
    </ThemeProvider>
  );
};

export default Layout;
